import { useState } from 'react';
import axios from 'axios';
import FadeIn from '../components/FadeIn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCE_OPTIONS = [
  'Yes',
  'Not fully, but open to exploring',
  'I would like to discuss this further',
];

const initialForm = {
  fullName: '', preferredName: '', email: '', phone: '',
  whatIsCalling: '', notSustainable: '', healingHistory: '',
  supportiveNotSupportive: '', howYouRespond: '', feelingSafe: '',
  drawsYouToContainer: '', hopingToExperience: '', resourceLevel: '',
  anythingElse: '',
};

const SectionHeader = ({ num, title }) => (
  <div className="mb-10">
    <div className="flex items-center gap-4 mb-2">
      <span className="font-serif text-3xl font-light" style={{ color: '#C6A96B' }}>{num}</span>
      <div style={{ width: '40px', height: '1px', background: '#C6A96B' }} />
    </div>
    <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: '#2B2B2B' }}>{title}</h2>
  </div>
);

const Question = ({ label, hint, children }) => (
  <div className="mb-10">
    <p className="font-serif text-lg mb-1" style={{ color: '#2B2B2B' }}>{label}</p>
    {hint && (
      <p className="text-xs mb-3" style={{ color: 'rgba(43,43,43,0.5)', fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}>
        {hint}
      </p>
    )}
    {children}
  </div>
);

const ApplicationPage = () => {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const required = ['fullName', 'email', 'phone', 'whatIsCalling', 'notSustainable',
      'healingHistory', 'supportiveNotSupportive', 'howYouRespond', 'feelingSafe',
      'drawsYouToContainer', 'hopingToExperience', 'resourceLevel'];
    const missing = required.filter((f) => !form[f]?.trim());
    if (missing.length > 0) {
      setError('Please complete all required fields before submitting.');
      window.scrollTo({ top: 400, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="application-success"
        className="min-h-screen flex items-center justify-center text-center px-6"
        style={{ background: '#F7F4EF', paddingTop: '120px', paddingBottom: '80px' }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8" style={{ width: '1px', height: '60px', background: '#C6A96B', margin: '0 auto 32px' }} />
          <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
            Received with Care
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl mb-8" style={{ color: '#2B2B2B' }}>
            Your Application Has Been Submitted
          </h1>
          <div className="space-y-4 mb-12">
            <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>
              Applications are reviewed with care and intention.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>
              If alignment is present, you will be invited into a conversation to explore fit and next steps.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>
              Thank you for taking this step.
            </p>
          </div>
          <a
            href="/"
            className="inline-block rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
            style={{ background: '#A8B5A2', color: '#2B2B2B', padding: '14px 36px', fontFamily: 'Inter, sans-serif' }}
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="application-page" style={{ background: '#F7F4EF', minHeight: '100vh' }}>

      {/* Header */}
      <div className="pt-40 pb-16 text-center" style={{ background: '#EAE3D9' }}>
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
            Private Container
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl px-6" style={{ color: '#2B2B2B', maxWidth: '700px', margin: '0 auto' }}>
            The Sovereign Integration Private Container Application
          </h1>
          <div className="max-w-2xl mx-auto px-6 mt-8">
            <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.7)', fontFamily: 'Inter, sans-serif' }}>
              Please complete this application to support a deeper understanding of where you are in your process and whether this container is an aligned fit.
            </p>
            <p className="text-sm mt-4 leading-relaxed" style={{ color: 'rgba(43,43,43,0.55)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
              You're invited to answer honestly and at your own pace. Thoughtful responses help create a more intentional and supportive experience.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        {error && (
          <div
            data-testid="form-error"
            className="mb-8 p-4 rounded-2xl text-sm"
            style={{ background: 'rgba(198,169,107,0.12)', border: '1px solid rgba(198,169,107,0.3)', color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="application-form" noValidate>

          {/* Section 2: Contact */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="01" title="Contact Information" />
            <div className="grid sm:grid-cols-2 gap-x-8">
              <Question label="Full Name">
                <input
                  className="form-input"
                  data-testid="input-fullName"
                  value={form.fullName}
                  onChange={handleChange('fullName')}
                  placeholder="Your full name"
                  required
                />
              </Question>
              <Question label="Preferred Name" hint="If different from above">
                <input
                  className="form-input"
                  data-testid="input-preferredName"
                  value={form.preferredName}
                  onChange={handleChange('preferredName')}
                  placeholder="What you'd like to be called"
                />
              </Question>
              <Question label="Email Address">
                <input
                  className="form-input"
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  placeholder="your@email.com"
                  required
                />
              </Question>
              <Question label="Phone Number">
                <input
                  className="form-input"
                  data-testid="input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  placeholder="Your phone number"
                  required
                />
              </Question>
            </div>
          </div>

          {/* Section 3: Current Experience */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="02" title="Current Experience" />
            <Question
              label="What is currently calling you to seek deeper, private support at this time?"
              hint="You're welcome to share any experiences, patterns, or moments that have led you here."
            >
              <textarea
                className="form-textarea"
                data-testid="input-whatIsCalling"
                value={form.whatIsCalling}
                onChange={handleChange('whatIsCalling')}
                placeholder="Share what feels alive and relevant for you right now..."
                rows={5}
                required
              />
            </Question>
            <Question label="What feels no longer sustainable in your current way of being?">
              <textarea
                className="form-textarea"
                data-testid="input-notSustainable"
                value={form.notSustainable}
                onChange={handleChange('notSustainable')}
                placeholder="What patterns, ways of being, or experiences feel like they can no longer continue..."
                rows={5}
                required
              />
            </Question>
          </div>

          {/* Section 4: Healing History */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="03" title="Your Healing History" />
            <Question
              label="What have you already explored in your healing or personal growth journey?"
              hint="This may include modalities, practitioners, practices, or personal exploration."
            >
              <textarea
                className="form-textarea"
                data-testid="input-healingHistory"
                value={form.healingHistory}
                onChange={handleChange('healingHistory')}
                placeholder="Share what you've explored and engaged with in your healing journey so far..."
                rows={5}
                required
              />
            </Question>
            <Question label="What has felt supportive—and what has not—in past healing or support spaces?">
              <textarea
                className="form-textarea"
                data-testid="input-supportiveNotSupportive"
                value={form.supportiveNotSupportive}
                onChange={handleChange('supportiveNotSupportive')}
                placeholder="Share what has worked for you and what hasn't in previous healing experiences..."
                rows={5}
                required
              />
            </Question>
          </div>

          {/* Section 5: Self-Awareness */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="04" title="Self-Awareness + Patterns" />
            <Question
              label="How do you typically respond when things slow down or deepen emotionally?"
              hint="For example: withdrawal, overthinking, shutdown, seeking support, etc."
            >
              <textarea
                className="form-textarea"
                data-testid="input-howYouRespond"
                value={form.howYouRespond}
                onChange={handleChange('howYouRespond')}
                placeholder="Describe your typical patterns when things get emotionally deep or slow..."
                rows={5}
                required
              />
            </Question>
            <Question
              label="What does feeling safe in a healing space look like for you?"
              hint="You may consider pacing, communication, boundaries, trust, or anything else that supports your sense of safety."
            >
              <textarea
                className="form-textarea"
                data-testid="input-feelingSafe"
                value={form.feelingSafe}
                onChange={handleChange('feelingSafe')}
                placeholder="What helps you feel safe, held, and supported in a healing container..."
                rows={5}
                required
              />
            </Question>
          </div>

          {/* Section 6: Container Alignment */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="05" title="Container Alignment" />
            <Question label="This is a long-term, relational container. What draws you to this level of support?">
              <textarea
                className="form-textarea"
                data-testid="input-drawsYouToContainer"
                value={form.drawsYouToContainer}
                onChange={handleChange('drawsYouToContainer')}
                placeholder="What calls you toward this kind of deep, sustained relational support..."
                rows={5}
                required
              />
            </Question>
            <Question label="What are you hoping to experience or shift through this work?">
              <textarea
                className="form-textarea"
                data-testid="input-hopingToExperience"
                value={form.hopingToExperience}
                onChange={handleChange('hopingToExperience')}
                placeholder="Share your intentions and what you hope to move toward through this container..."
                rows={5}
                required
              />
            </Question>
          </div>

          {/* Section 7: Readiness */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="06" title="Readiness" />
            <Question label="This container requires emotional, energetic, and financial capacity. How resourced do you currently feel to engage in this level of support?">
              <div className="space-y-4 mt-2" data-testid="resource-level-options">
                {RESOURCE_OPTIONS.map((option) => (
                  <label
                    key={option}
                    data-testid={`resource-option-${option.replace(/\s+/g, '-').toLowerCase()}`}
                    className="flex items-center gap-4 p-5 rounded-2xl cursor-pointer transition-all duration-200"
                    style={{
                      background: form.resourceLevel === option ? 'rgba(168,181,162,0.18)' : 'rgba(234,227,217,0.5)',
                      border: form.resourceLevel === option ? '1.5px solid #A8B5A2' : '1.5px solid transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      className="flex-shrink-0 rounded-full"
                      style={{
                        width: '18px', height: '18px',
                        border: '1.5px solid #A8B5A2',
                        background: form.resourceLevel === option ? '#A8B5A2' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {form.resourceLevel === option && (
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#F7F4EF' }} />
                      )}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      name="resourceLevel"
                      value={option}
                      checked={form.resourceLevel === option}
                      onChange={handleChange('resourceLevel')}
                    />
                    <span className="text-sm" style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}>{option}</span>
                  </label>
                ))}
              </div>
            </Question>
          </div>

          {/* Section 8: Final Share */}
          <div className="mb-16 pb-16" style={{ borderBottom: '1px solid #EAE3D9' }}>
            <SectionHeader num="07" title="Final Share" />
            <Question
              label="Is there anything else you feel would be important for me to understand about you or your current experience?"
              hint="You're welcome to share anything that feels relevant to your context, needs, or support."
            >
              <textarea
                className="form-textarea"
                data-testid="input-anythingElse"
                value={form.anythingElse}
                onChange={handleChange('anythingElse')}
                placeholder="Any additional context, needs, or reflections you'd like to share..."
                rows={6}
              />
            </Question>
          </div>

          {/* Closing */}
          <div className="mb-14 text-center py-8">
            <div style={{ width: '1px', height: '40px', background: '#C6A96B', margin: '0 auto 20px' }} />
            <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(43,43,43,0.65)', fontFamily: 'Inter, sans-serif' }}>
              Applications are reviewed with care and intention.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.65)', fontFamily: 'Inter, sans-serif' }}>
              If alignment is present, you will be invited into a conversation to explore fit and next steps.
            </p>
          </div>

          <div className="text-center">
            <button
              type="submit"
              data-testid="submit-application-btn"
              disabled={loading}
              className="rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85 disabled:opacity-50"
              style={{
                background: loading ? '#A8B5A2' : '#7E8C74',
                color: '#F7F4EF',
                padding: '18px 56px',
                fontFamily: 'Inter, sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ApplicationPage;
